// One run: outcome, timing and the log.
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useAbortRunMutation, useSchedulerRunQuery } from '@/client/api/scheduler/scheduler.query';
import {
  AccessDenied,
  Card,
  ErrorNotice,
  Field,
  OutcomeBadge,
  PageShell,
  ReasonDialog,
  SCHEDULER_BASE,
  fmtBytes,
  fmtDur,
  fmtTime,
  isOk,
} from './schedulerUi';

const PERF_KEYS: [string, string][] = [
  ['dns', 'DNS'],
  ['connect', 'Connect'],
  ['send', 'Send'],
  ['wait', 'Wait'],
  ['receive', 'Receive'],
  ['total', 'Total'],
];

/** Cronicle's http plugin reports perf as numbers (ms); ignore anything else. */
const perfTimes = (perf: unknown): { label: string; ms: number }[] => {
  const inner = (perf as { perf?: unknown } | null)?.perf;
  if (!inner || typeof inner !== 'object') return [];
  const src = inner as Record<string, unknown>;
  return PERF_KEYS.filter(([k]) => typeof src[k] === 'number').map(([k, label]) => ({
    label,
    ms: src[k] as number,
  }));
};

const SchedulerRunPage: React.FC = () => {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const viewPerm = useHasPermission('scheduler.view');
  const operatePerm = useHasPermission('scheduler.operate');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canOperate = operatePerm.isSuperAdmin || operatePerm.hasPermission;
  const q = useSchedulerRunQuery(jobId, canView);
  const abort = useAbortRunMutation();
  const [aborting, setAborting] = useState(false);
  const r = q.data;

  useBreadcrumbs([
    { label: 'Scheduler', href: SCHEDULER_BASE },
    ...(r
      ? [{ label: r.event_title, href: `${SCHEDULER_BASE}/jobs/${encodeURIComponent(r.event)}` }]
      : []),
    { label: 'Run' },
  ]);

  if (viewPerm.isLoading) {
    return (
      <PageShell title="Run" description="">
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Run" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!r) {
    return (
      <PageShell title="Run" description="">
        {q.isError ? (
          <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </PageShell>
    );
  }

  const perf = perfTimes(r.perf);
  const close = () => {
    setAborting(false);
    abort.reset();
  };
  return (
    <PageShell
      title={r.event_title}
      description={`Run ${r.id}`}
      actions={
        !r.complete && canOperate ? (
          <Button size="sm" variant="destructive" onClick={() => setAborting(true)}>
            Abort run
          </Button>
        ) : undefined
      }
    >
      <Card title="Outcome">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Field label="Result">
            {r.complete ? (
              <OutcomeBadge code={r.code} />
            ) : (
              <span className="text-blue-700">Running</span>
            )}
          </Field>
          <Field label="Job">
            <Link
              to={`${SCHEDULER_BASE}/jobs/${encodeURIComponent(r.event)}`}
              className="text-blue-600 hover:underline"
            >
              {r.event_title}
            </Link>
          </Field>
          <Field label="Started">{fmtTime(r.time_start)}</Field>
          <Field label="Ended">{r.complete ? fmtTime(r.time_end) : '—'}</Field>
          <Field label="Duration">{fmtDur(r.elapsed)}</Field>
          <Field label="Host">{r.hostname}</Field>
          <Field label="Started by">{r.source || '—'}</Field>
          <Field label="Retries used">{r.retries}</Field>
          <Field label="Request">
            <span className="font-mono text-xs">
              {r.request?.method} {r.request?.url}
            </span>
          </Field>
        </div>
        {r.complete && !isOk(r.code) && r.description && (
          <p className="mt-3 break-all rounded-lg bg-red-50 p-2 font-mono text-xs text-red-700">
            {r.description}
          </p>
        )}
      </Card>

      {perf.length > 0 && (
        <Card title="Timing">
          <div className="grid grid-cols-3 gap-4 md:grid-cols-6">
            {perf.map((t) => (
              <Field key={t.label} label={t.label}>
                {Math.round(t.ms)} ms
              </Field>
            ))}
          </div>
        </Card>
      )}

      <Card title="Log" note={fmtBytes(r.log_file_size)}>
        {r.log.truncated && (
          <p className="mb-2 text-xs text-gray-500">Showing the end of the log</p>
        )}
        {!r.log.available ? (
          <p className="text-sm text-gray-400">The log is not available for this run.</p>
        ) : (
          <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-100">
            {r.log.text || '(empty)'}
          </pre>
        )}
      </Card>

      {aborting && (
        <ReasonDialog
          open
          onClose={close}
          title={`Abort ${r.event_title}`}
          description="Stops this run now. The job keeps its schedule."
          confirmLabel="Abort run"
          destructive
          busy={abort.isPending}
          error={abort.error}
          onConfirm={(reason) =>
            abort.mutate({ jobId: r.id, reason: reason || undefined }, { onSuccess: close })
          }
        />
      )}
    </PageShell>
  );
};

export default SchedulerRunPage;
